import { Controller } from "@hotwired/stimulus"

// Final create-order confirmation gate for the PO review screen. Mirrors
// po-family-guard: a capture-phase submit interceptor that pops a single
// <dialog> the operator must acknowledge before the order is created —
// but ONLY when at least one line carries real risk:
//
//   * a WEAK auto-match (the amber "Review match" badge — match_confidence
//     == :weak from the builder) that the operator left pointing at the
//     guessed product. This is the production bug: a confidently-wrong
//     match (e.g. SKU CHG-01 for a totally different film) sailed through
//     with no acknowledgement.
//
// (Brand-new standalone products are already covered by po-family-guard +
// the po-submit-status banner, so they're not re-gated here.) Clean orders
// with no weak match submit straight through — the gate stays out of the
// way until there's something to confirm.
//
// Flow (identical bypass handshake to po-family-guard so the two — plus
// po-remap-confirm / po-submit-status — coexist on the same form):
//   1. submit fires `check` (capture phase, runs before Turbo).
//   2. collect risky rows; if none, let the submit proceed.
//   3. otherwise preventDefault, render the rows into the dialog, showModal.
//   4. "Go back & review" → close, do nothing.
//      "Create order"     → set the hidden confirmed-create field (the
//                           server backstop reads it), flip _bypass, and
//                           requestSubmit so the form sails through.
//
// Targets:
//   dialog    - the <dialog>
//   rowList   - <ul> we fill with the risky line descriptions
//   confirmed - hidden input the server backstop checks (confirmed_create)
export default class extends Controller {
    static targets = ["dialog", "rowList", "confirmed"]

    connect() {
        this._check = this._check.bind(this)
        this.element.addEventListener("submit", this._check, { capture: true })
        this._bypass = false
    }

    disconnect() {
        this.element.removeEventListener("submit", this._check, { capture: true })
    }

    _check(event) {
        if (this._bypass) {
            this._bypass = false
            return
        }

        const risky = this._collectRisky()
        if (risky.length === 0) return

        event.preventDefault()
        event.stopPropagation()
        this._render(risky)
        if (this.hasDialogTarget && this.dialogTarget.showModal) {
            this.dialogTarget.showModal()
        }
    }

    cancel() {
        if (this.hasDialogTarget) this.dialogTarget.close()
    }

    proceed() {
        if (this.hasDialogTarget) this.dialogTarget.close()
        if (this.hasConfirmedTarget) this.confirmedTarget.value = "1"
        this._bypass = true
        this.element.requestSubmit()
    }

    // ── private ─────────────────────────────────────────────────────────

    _collectRisky() {
        const rows = this.element.querySelectorAll('[data-controller~="po-row"]')
        const risky = []
        rows.forEach((row) => {
            const productSelect = row.querySelector('select[name*="[product_id]"]')
            const confidence    = row.getAttribute("data-po-row-match-confidence")

            // Weak auto-match the operator left pointing at the guessed
            // product. If they changed the dropdown to a different product
            // the weak signal no longer applies — they made a deliberate
            // pick — so only flag when the select still holds a value.
            if (confidence === "weak" && productSelect && productSelect.value !== "") {
                risky.push({ desc: this._describe(row) })
            }
        })
        return risky
    }

    _describe(row) {
        const cell = row.querySelector("td")
        return cell ? cell.textContent.trim().split("\n")[0] : "(line item)"
    }

    _render(risky) {
        if (!this.hasRowListTarget) return
        this.rowListTarget.innerHTML = ""
        risky.forEach(({ desc }) => {
            const li = document.createElement("li")
            li.style.marginBottom = "0.375rem"
            li.innerHTML = `<strong>${this._escape(desc)}</strong> &mdash; <em>review this auto-match</em>`
            this.rowListTarget.appendChild(li)
        })
    }

    _escape(str) {
        const div = document.createElement("div")
        div.textContent = str
        return div.innerHTML
    }
}
